<template>
    <div class="alerts-page">
        <div class="page-header">
            <h1>Alertes de messages</h1>
            <p class="subtitle">Messages vendeurs sans réponse depuis plus de {{ alertDelayHours }} heures</p>
        </div>

        <!-- Filters -->
        <div class="filters-bar">
            <select v-model="countryFilter" class="filter-select" @change="loadAlerts">
                <option value="">Tous les pays</option>
                <option v-for="country in countries" :key="country.code" :value="country.code">
                    {{ country.name }}
                </option>
            </select>

            <select v-model="statusFilter" class="filter-select" @change="loadAlerts">
                <option value="pending">En attente</option>
                <option value="resolved">Résolues</option>
                <option value="all">Toutes</option>
            </select>

            <button class="refresh-btn" @click="loadAlerts" :disabled="loading">
                <i class="material-icons" :class="{ 'spinning': loading }">refresh</i>
                <span>Actualiser</span>
            </button>
        </div>

        <!-- Stats Summary -->
        <div class="stats-summary">
            <div class="stat-item warning">
                <i class="material-icons">warning</i>
                <div class="stat-content">
                    <span class="stat-value">{{ pendingAlertsCount }}</span>
                    <span class="stat-label">Alertes en attente</span>
                </div>
            </div>
            <div class="stat-item success">
                <i class="material-icons">check_circle</i>
                <div class="stat-content">
                    <span class="stat-value">{{ resolvedTodayCount }}</span>
                    <span class="stat-label">Résolues aujourd'hui</span>
                </div>
            </div>
            <div class="stat-item info">
                <i class="material-icons">schedule</i>
                <div class="stat-content">
                    <span class="stat-value">{{ avgResponseTime }}</span>
                    <span class="stat-label">Temps moyen de réponse</span>
                </div>
            </div>
        </div>

        <!-- Alerts List -->
        <div v-if="loading" class="loading-state">
            <div class="spinner"></div>
            <span>Chargement des alertes...</span>
        </div>

        <div v-else-if="alerts.length === 0" class="empty-state">
            <i class="material-icons">notifications_off</i>
            <h3>Aucune alerte</h3>
            <p v-if="statusFilter === 'pending'">
                Tous les messages ont été traités
            </p>
            <p v-else>
                Aucune alerte trouvée avec les filtres sélectionnés
            </p>
        </div>

        <div v-else class="alerts-list">
            <div
                v-for="alert in alerts"
                :key="alert._id"
                class="alert-card"
                :class="{ resolved: alert.resolved }"
            >
                <div class="alert-header">
                    <div class="am-info">
                        <div class="am-avatar">
                            <span>{{ getInitial(alert.accountManager) }}</span>
                        </div>
                        <div class="am-details">
                            <span class="am-name">{{ getAMName(alert.accountManager) }}</span>
                            <span class="am-role">Account Manager</span>
                        </div>
                    </div>
                    <div class="alert-status">
                        <span class="status-badge" :class="alert.resolved ? 'resolved' : 'pending'">
                            {{ alert.resolved ? 'Résolu' : 'En attente' }}
                        </span>
                    </div>
                </div>

                <div class="alert-body">
                    <div class="seller-row">
                        <span class="label">Vendeur:</span>
                        <span class="value">{{ getSellerName(alert.conversation && alert.conversation.seller) }}</span>
                    </div>
                    <div class="country-row">
                        <span class="label">Pays:</span>
                        <span class="value">{{ (alert.conversation && alert.conversation.country) || 'N/A' }}</span>
                    </div>
                    <div class="time-row">
                        <span class="label">Dernier message vendeur:</span>
                        <span class="value time">{{ formatDateTime(alert.lastSellerMessageAt) }}</span>
                    </div>
                    <div class="waiting-row">
                        <span class="label">En attente depuis:</span>
                        <span class="value waiting" :class="getWaitingClass(alert.waitingHours)">
                            {{ formatWaitingTime(alert.waitingHours) }}
                        </span>
                    </div>
                </div>

                <div class="alert-actions">
                    <button
                        class="action-btn view"
                        @click="viewConversation(alert.conversation && alert.conversation._id)"
                    >
                        <i class="material-icons">visibility</i>
                        <span>Voir la conversation</span>
                    </button>
                    <button
                        v-if="!alert.resolved"
                        class="action-btn contact"
                        @click="contactAM(alert.accountManager)"
                    >
                        <i class="material-icons">email</i>
                        <span>Contacter l'AM</span>
                    </button>
                    <button
                        v-if="!alert.resolved"
                        class="action-btn resolve"
                        @click="markAsResolved(alert._id)"
                    >
                        <i class="material-icons">check</i>
                        <span>Marquer résolu</span>
                    </button>
                </div>
            </div>
        </div>

        <!-- Pagination -->
        <div v-if="totalPages > 1" class="pagination">
            <button
                class="page-btn"
                :disabled="currentPage === 1"
                @click="changePage(currentPage - 1)"
            >
                <i class="material-icons">chevron_left</i>
            </button>
            <span class="page-info">Page {{ currentPage }} sur {{ totalPages }}</span>
            <button
                class="page-btn"
                :disabled="currentPage === totalPages"
                @click="changePage(currentPage + 1)"
            >
                <i class="material-icons">chevron_right</i>
            </button>
        </div>
    </div>
</template>

<script>
export default {
    name: 'ChatAlerts',
    data() {
        return {
            alerts: [],
            loading: false,
            countryFilter: '',
            statusFilter: 'pending',
            countries: [],
            currentPage: 1,
            pageSize: 10,
            totalPages: 1,
            totalAlerts: 0,
            pendingAlertsCount: 0,
            resolvedTodayCount: 0,
            avgResponseTime: '0h'
        };
    },
    computed: {
        alertDelayHours() {
            const settings = this.$store.state.settingsGlobal;
            return (settings && settings.chat && settings.chat.alertDelayHours) || 4;
        }
    },
    async mounted() {
        await this.loadCountries();
        await this.loadAlerts();
        await this.loadStats();
    },
    methods: {
        async loadCountries() {
            try {
                var response = await this.$server.search('warehouses', { all: true, limit: 100 });
                if (response && response.content && response.content.results) {
                    var countriesMap = {};
                    response.content.results.forEach(function(warehouse) {
                        if (warehouse.country) {
                            countriesMap[warehouse.country] = {
                                code: warehouse.country,
                                name: warehouse.countryName || warehouse.name || warehouse.country
                            };
                        }
                    });
                    this.countries = Object.values(countriesMap).sort(function(a, b) {
                        return a.name.localeCompare(b.name);
                    });
                }
            } catch (error) {
                console.error('Failed to load countries:', error);
                this.countries = [];
            }
        },
        async loadAlerts() {
            this.loading = true;
            try {
                const params = {
                    page: this.currentPage,
                    limit: this.pageSize
                };

                if (this.countryFilter) {
                    params.country = this.countryFilter;
                }
                if (this.statusFilter !== 'all') {
                    params.resolved = this.statusFilter === 'resolved';
                }

                const response = await this.$server.search('conversations', {
                    alertTriggered: true,
                    ...params
                });

                if (response.content) {
                    this.alerts = response.content.results || [];
                    this.totalAlerts = response.content.total || 0;
                    this.totalPages = Math.ceil(this.totalAlerts / this.pageSize);
                }
            } catch (error) {
                console.error('Failed to load alerts:', error);
                if (this.$toast) this.$toast.error('Erreur lors du chargement des alertes');
            } finally {
                this.loading = false;
            }
        },
        async loadStats() {
            try {
                const response = await this.$server.getUnansweredAlerts('conversations');
                if (response && response.content) {
                    this.pendingAlertsCount = (response.content.results && response.content.results.length) || 0;
                    this.resolvedTodayCount = 0; // Will be calculated from alerts if needed
                    this.avgResponseTime = '< 24h';
                }
            } catch (error) {
                console.error('Failed to load alert stats:', error);
            }
        },
        getInitial(user) {
            if (!user) return '?';
            const name = user.fullName || user.firstName || user.email || '';
            return name.charAt(0).toUpperCase();
        },
        getAMName(am) {
            if (!am) return 'Account Manager';
            return am.fullName || `${am.firstName || ''} ${am.lastName || ''}`.trim() || am.email;
        },
        getSellerName(seller) {
            if (!seller) return 'Vendeur';
            return seller.fullName || `${seller.firstName || ''} ${seller.lastName || ''}`.trim() || seller.email;
        },
        formatDateTime(dateStr) {
            if (!dateStr) return '-';
            return new Date(dateStr).toLocaleString('fr-FR', {
                day: 'numeric',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit'
            });
        },
        formatWaitingTime(hours) {
            if (!hours) return '< 1h';
            if (hours < 1) return '< 1h';
            if (hours < 24) return `${Math.round(hours)}h`;
            const days = Math.floor(hours / 24);
            const remainingHours = Math.round(hours % 24);
            return `${days}j ${remainingHours}h`;
        },
        formatDuration(minutes) {
            if (!minutes) return '0h';
            if (minutes < 60) return `${Math.round(minutes)}min`;
            const hours = Math.floor(minutes / 60);
            const mins = Math.round(minutes % 60);
            return `${hours}h ${mins}min`;
        },
        getWaitingClass(hours) {
            if (hours >= 24) return 'critical';
            if (hours >= 12) return 'warning';
            return 'normal';
        },
        viewConversation(conversationId) {
            if (conversationId) {
                this.$router.push(`/account-manager/chat/${conversationId}`);
            }
        },
        contactAM(am) {
            if (am && am.email) {
                window.location.href = `mailto:${am.email}?subject=Alerte message non répondu`;
            }
        },
        async markAsResolved(alertId) {
            try {
                await this.$server.update('conversations', {
                    _id: alertId,
                    alertTriggered: false
                });

                // Refresh list
                await this.loadAlerts();
                await this.loadStats();

                if (this.$toast) this.$toast.success('Alerte marquée comme résolue');
            } catch (error) {
                console.error('Failed to mark as resolved:', error);
                if (this.$toast) this.$toast.error('Erreur lors de la mise à jour');
            }
        },
        changePage(page) {
            if (page >= 1 && page <= this.totalPages) {
                this.currentPage = page;
                this.loadAlerts();
            }
        }
    }
};
</script>

<style scoped>
.alerts-page {
    padding: 24px;
}

.page-header {
    margin-bottom: 24px;
}

.page-header h1 {
    margin: 0;
    font-size: 28px;
    font-weight: 700;
    color: #111827;
}

.subtitle {
    margin: 4px 0 0;
    color: #6b7280;
    font-size: 14px;
}

.filters-bar {
    display: flex;
    gap: 12px;
    margin-bottom: 24px;
    flex-wrap: wrap;
}

.filter-select {
    padding: 10px 16px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 14px;
    background-color: white;
    cursor: pointer;
    outline: none;
}

.filter-select:focus {
    border-color: #059669;
}

.refresh-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    background-color: #f3f4f6;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    color: #374151;
    transition: all 0.2s;
}

.refresh-btn:hover:not(:disabled) {
    background-color: #e5e7eb;
}

.refresh-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.refresh-btn i.spinning {
    animation: spin 1s linear infinite;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

.stats-summary {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
    margin-bottom: 24px;
}

.stat-item {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 20px;
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.stat-item i {
    font-size: 32px;
}

.stat-item.warning i { color: #f59e0b; }
.stat-item.success i { color: #10b981; }
.stat-item.info i { color: #3b82f6; }

.stat-content {
    display: flex;
    flex-direction: column;
}

.stat-value {
    font-size: 24px;
    font-weight: 700;
    color: #111827;
}

.stat-label {
    font-size: 13px;
    color: #6b7280;
}

.loading-state,
.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 300px;
    color: #9ca3af;
}

.spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #e5e7eb;
    border-top-color: #059669;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin-bottom: 16px;
}

.empty-state i {
    font-size: 64px;
    margin-bottom: 16px;
    opacity: 0.5;
}

.empty-state h3 {
    margin: 0 0 8px;
    font-size: 18px;
    color: #6b7280;
}

.alerts-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.alert-card {
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    border-left: 4px solid #ef4444;
}

.alert-card.resolved {
    border-left-color: #10b981;
    opacity: 0.8;
}

.alert-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    background-color: #f9fafb;
    border-bottom: 1px solid #e5e7eb;
}

.am-info {
    display: flex;
    align-items: center;
    gap: 12px;
}

.am-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 600;
    font-size: 16px;
}

.am-details {
    display: flex;
    flex-direction: column;
}

.am-name {
    font-weight: 600;
    color: #111827;
    font-size: 14px;
}

.am-role {
    font-size: 12px;
    color: #6b7280;
}

.status-badge {
    padding: 6px 12px;
    border-radius: 16px;
    font-size: 12px;
    font-weight: 500;
}

.status-badge.pending {
    background-color: #fef3c7;
    color: #d97706;
}

.status-badge.resolved {
    background-color: #ecfdf5;
    color: #059669;
}

.alert-body {
    padding: 16px 20px;
}

.alert-body > div {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
}

.alert-body > div:last-child {
    margin-bottom: 0;
}

.label {
    font-size: 13px;
    color: #6b7280;
    width: 180px;
    flex-shrink: 0;
}

.value {
    font-size: 14px;
    color: #111827;
    font-weight: 500;
}

.value.time {
    color: #6b7280;
}

.value.waiting {
    font-weight: 600;
}

.value.waiting.normal {
    color: #059669;
}

.value.waiting.warning {
    color: #f59e0b;
}

.value.waiting.critical {
    color: #ef4444;
}

.alert-actions {
    display: flex;
    gap: 8px;
    padding: 16px 20px;
    border-top: 1px solid #e5e7eb;
    flex-wrap: wrap;
}

.action-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border: none;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
}

.action-btn i {
    font-size: 18px;
}

.action-btn.view {
    background-color: #eff6ff;
    color: #3b82f6;
}

.action-btn.view:hover {
    background-color: #dbeafe;
}

.action-btn.contact {
    background-color: #f3f4f6;
    color: #374151;
}

.action-btn.contact:hover {
    background-color: #e5e7eb;
}

.action-btn.resolve {
    background-color: #ecfdf5;
    color: #059669;
}

.action-btn.resolve:hover {
    background-color: #d1fae5;
}

.pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    margin-top: 32px;
}

.page-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #ffffff;
    cursor: pointer;
    transition: all 0.2s;
}

.page-btn:hover:not(:disabled) {
    background-color: #f3f4f6;
}

.page-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.page-info {
    font-size: 14px;
    color: #6b7280;
}

@media (max-width: 768px) {
    .alerts-page {
        padding: 16px;
    }

    .filters-bar {
        flex-direction: column;
    }

    .filter-select,
    .refresh-btn {
        width: 100%;
    }

    .alert-actions {
        flex-direction: column;
    }

    .action-btn {
        width: 100%;
        justify-content: center;
    }

    .label {
        width: 140px;
    }
}
</style>
